/**
 * Brand パネル — ロゴ、Primary/Accent カラー、フォント、SNS リンク。
 *
 * Primary color / Accent color はランディング全体のテーマを上書きする:
 *   - Primary color → `--ls-text` (本文テキスト色)
 *   - Accent color  → `--ls-accent` (ボタン・リンク・強調色)
 * 空にすると visualIdentity の既定値に戻る。
 *
 * 入力は HEX (#0F0F0E 形式) を期待。カラーピッカーとテキスト入力の両方を提供。
 */
import { useEffect, useRef, useState } from 'react';

import { Input, Label } from '@/components/ui/input';
import { useUpdateLanding, type LandingRecord } from '@/hooks/useLandings';

import { ImageFieldEditor } from './ImageFieldEditor';

interface Props {
  landingId: string;
  landing: LandingRecord;
}

const SOCIAL_PLATFORMS = ['twitter', 'facebook', 'instagram', 'youtube', 'linkedin'];

export function BrandPanel({ landingId, landing }: Props) {
  const updateLanding = useUpdateLanding();
  const settings = landing.brandSettings ?? {
    logoStoragePath: null,
    primaryColor: null,
    accentColor: null,
    headingFont: null,
    bodyFont: null,
    socialLinks: {},
  };

  const update = (patch: Partial<LandingRecord['brandSettings']>) => {
    updateLanding.mutateAsync({
      id: landingId,
      data: {
        brandSettings: {
          ...settings,
          ...patch,
        },
      },
    });
  };

  return (
    <div className="space-y-3">
      <ImageFieldEditor
        label="ブランドロゴ"
        value={settings.logoStoragePath}
        onChange={(v) => update({ logoStoragePath: v })}
        storagePath="marketing/lp/imgs"
      />

      <div className="grid grid-cols-2 gap-2">
        <ColorField
          label="メインカラー"
          value={settings.primaryColor ?? ''}
          placeholder="#0F0F0E"
          onSave={(v) => update({ primaryColor: v || null })}
        />
        <ColorField
          label="アクセントカラー"
          value={settings.accentColor ?? ''}
          placeholder="#3D5AFE"
          onSave={(v) => update({ accentColor: v || null })}
        />
      </div>
      <p className="text-[10px] text-[var(--text-muted)] -mt-2">
        色を設定すると、選択中のビジュアルテーマの色を上書きします。空欄でテーマ既定値に戻ります。
      </p>

      <BlurField
        label="見出しフォント"
        value={settings.headingFont ?? ''}
        placeholder="例: Inter, system-ui / Geist, sans-serif"
        onSave={(v) => update({ headingFont: v || null })}
      />
      <BlurField
        label="本文フォント"
        value={settings.bodyFont ?? ''}
        placeholder="例: Inter, system-ui"
        onSave={(v) => update({ bodyFont: v || null })}
      />

      <div>
        <Label className="text-xs">SNS リンク (フッターで使用)</Label>
        <div className="space-y-1.5">
          {SOCIAL_PLATFORMS.map((p) => (
            <div key={p} className="flex items-center gap-2">
              <span className="text-[11px] text-[var(--text-muted)] w-16 capitalize">
                {p}
              </span>
              <BlurField
                value={settings.socialLinks?.[p] ?? ''}
                placeholder="https://..."
                onSave={(v) =>
                  update({
                    socialLinks: {
                      ...(settings.socialLinks ?? {}),
                      [p]: v,
                    },
                  })
                }
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * カラーピッカー + 16進テキスト入力のハイブリッドフィールド。
 * - 左: カラーピッカー (input[type=color]) — クリックでネイティブピッカー起動
 * - 右: テキスト入力 — `#RRGGBB` を手入力可能
 * - 「クリア」ボタンで未設定 (テーマ既定値に戻す) に
 * 値は常に `#RRGGBB` 形式で保存（input[type=color] が要求する形式）。
 */
function ColorField({
  label,
  value,
  placeholder,
  onSave,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onSave: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  const composing = useRef(false);

  // input[type=color] は #RRGGBB を必須。未設定/不正は #000000 にフォールバック。
  const pickerValue = /^#[0-9a-fA-F]{6}$/.test(local) ? local : '#000000';

  const handlePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setLocal(v);
    onSave(v);
  };

  const handleTextBlur = () => {
    if (composing.current) return;
    if (local !== value) onSave(local);
  };

  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-stretch gap-1.5">
        {/* カラーピッカー (ネイティブ) */}
        <div className="relative shrink-0">
          <input
            type="color"
            value={pickerValue}
            onChange={handlePickerChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            aria-label={`${label}を選ぶ`}
          />
          <div
            className="w-9 h-9 rounded border border-[var(--border)]"
            style={{
              background: /^#[0-9a-fA-F]{6}$/.test(local) ? local : 'var(--bg-elevated)',
              backgroundImage: /^#[0-9a-fA-F]{6}$/.test(local)
                ? undefined
                : 'linear-gradient(45deg, transparent 49%, #ccc 49%, #ccc 51%, transparent 51%)',
            }}
            title={local || '未設定'}
          />
        </div>
        {/* 16進テキスト入力 */}
        <Input
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={handleTextBlur}
          onCompositionStart={() => (composing.current = true)}
          onCompositionEnd={() => (composing.current = false)}
          placeholder={placeholder}
          className="font-mono text-xs flex-1"
        />
      </div>
      {local && (
        <button
          type="button"
          onClick={() => {
            setLocal('');
            onSave('');
          }}
          className="text-[10px] text-[var(--text-muted)] hover:text-red-500 mt-0.5"
        >
          クリア (テーマ既定値に戻す)
        </button>
      )}
    </div>
  );
}

function BlurField({
  label,
  value,
  placeholder,
  onSave,
}: {
  label?: string;
  value: string;
  placeholder?: string;
  onSave: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  const composing = useRef(false);
  const input = (
    <Input
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (composing.current) return;
        if (local !== value) onSave(local);
      }}
      onCompositionStart={() => (composing.current = true)}
      onCompositionEnd={() => (composing.current = false)}
      placeholder={placeholder}
    />
  );
  if (!label) return input;
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {input}
    </div>
  );
}
